import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { connectSocket, disconnectSocket } from '../services';

/**
 * Custom hook that manages WebSocket connection lifecycle based on authentication state.
 *
 * Automatically establishes WebSocket connections when authenticated and cleans up
 * when the user logs out or the component unmounts.
 */
const useSocketConnection = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // We use ref to avoid multiple connections to the socket
  const isConnectedRef = useRef(false);

  useEffect(() => {
    const initializeSocket = async () => {
      if (isAuthenticated && !isConnectedRef.current) {
        try {
          const token = await getAccessTokenSilently();
          // Establish WebSocket connection with authentication token
          connectSocket(token);
          isConnectedRef.current = true;
        } catch (error) {
          console.error('Failed to initialize socket connection:', error);
        }
      } else if (!isAuthenticated && isConnectedRef.current) {
        // Disconnect when user logs out
        disconnectSocket();
        isConnectedRef.current = false;
      }
    };

    initializeSocket();

    // Cleanup function to avoid multiple connections to the socket
    return () => {
      if (isConnectedRef.current) {
        disconnectSocket();
        isConnectedRef.current = false;
      }
    };
  }, [isAuthenticated, getAccessTokenSilently]);
};

export default useSocketConnection;
